/**
 * The fields a connection is made of, shared by Add and Edit: name, base URL,
 * then whatever its connector's `settings_schema` asks for, rendered generically.
 * Every field is stored encrypted and typed here once. On edit a revealed one
 * (a client ID, a header name) starts filled with its saved value; a hidden one
 * starts blank, shows its masked hint as the placeholder, and blank means
 * "keep what is saved".
 */

import { Field } from "@/components/ui/field";
import type { Connector, SettingField } from "@/features/connectors/types";
import type { Connection } from "./types";

export interface ConnectionDraft {
  name: string;
  base_url: string;
  /** Every schema field by key; a missing key shows its saved or default value. */
  values: Record<string, string>;
}

export const emptyDraft = (): ConnectionDraft => ({ name: "", base_url: "", values: {} });

export function draftOf(c: Connection): ConnectionDraft {
  return { name: c.name, base_url: c.base_url, values: {} };
}

/** The name and base URL copy for each platform; others read as Orchestrator. */
interface Copy {
  name: string;
  urlLabel?: string;
  url: string;
  urlHint: string;
}

const COPY: Record<string, Copy> = {
  qflow: {
    name: "qFlow beta",
    urlLabel: "Workflow API URL",
    url: "https://qflow-workflow-api-beta.qure.ai",
    urlHint:
      "qFlow's workflow API, whose runs are read. Read only: nothing is run there. The workbench signs in with its own AWS identity; each dataset picks its workspace.",
  },
  qflow_workflow: {
    name: "qFlow beta workflows",
    urlLabel: "Workflow API URL",
    url: "https://qflow-workflow-api-beta.qure.ai",
    urlHint:
      "qFlow's workflow API. Each test case is one dry run: nothing is saved in qFlow. The workbench signs in with its own AWS identity; each agent names its workspace.",
  },
  langfuse: {
    name: "Langfuse prod",
    url: "https://langfuse.example.com",
    urlHint: "The Langfuse the traces are read from.",
  },
};
const DEFAULT_COPY: Copy = {
  name: "Orchestrator staging",
  url: "https://orchestrator-staging.example.com",
  urlHint: "The deployment the workbench calls.",
};

/** What the saved connection lets the field start with: a revealed field's value. */
const savedOf = (f: SettingField, saved: Connection | undefined) =>
  f.reveal ? (saved?.secret[f.key] ?? "") : "";

const valueOf = (draft: ConnectionDraft, f: SettingField, saved?: Connection) =>
  draft.values[f.key] ?? (saved ? savedOf(f, saved) : (f.default ?? ""));

/** The fields typed in; on edit the blank and unchanged ones are left out, so they are kept. */
export function secretOf(
  connector: Connector,
  draft: ConnectionDraft,
  saved?: Connection,
): Record<string, string> {
  const typed = connector.settings_schema
    .map((f) => [f.key, valueOf(draft, f, saved).trim(), savedOf(f, saved)] as const)
    .filter(([, v, kept]) => v && v !== kept)
    .map(([k, v]) => [k, v]);
  return Object.fromEntries(typed);
}

/** Whether the draft can be saved (the server re-checks every rule). */
export function draftReady(connector: Connector, draft: ConnectionDraft, saved?: Connection): boolean {
  if (!draft.name.trim() || !draft.base_url.trim()) return false;
  return connector.settings_schema.every(
    (f) => !f.required || !!valueOf(draft, f, saved).trim() || !!saved?.secret[f.key],
  );
}

/** A hidden field's saved mask (``••••259e``), shown in its empty input. */
const placeholderOf = (f: SettingField, saved: Connection | undefined) =>
  f.reveal ? undefined : saved?.secret[f.key];

export function ConnectionFields({
  connector,
  draft,
  saved,
  onChange,
}: {
  connector: Connector;
  draft: ConnectionDraft;
  /** The connection being edited, for its saved secrets. */
  saved?: Connection | undefined;
  onChange: (draft: ConnectionDraft) => void;
}) {
  const copy = COPY[connector.type] ?? DEFAULT_COPY;
  const set = (key: string, value: string) =>
    onChange({ ...draft, values: { ...draft.values, [key]: value } });
  return (
    <>
      <Field label="Name" required htmlFor="connection-name" hint={`For example “${copy.name}”.`}>
        <input
          id="connection-name"
          className="inp"
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
        />
      </Field>
      <Field label={copy.urlLabel ?? "Base URL"} required htmlFor="connection-base-url" hint={copy.urlHint}>
        <input
          id="connection-base-url"
          className="inp font-mono"
          placeholder={copy.url}
          value={draft.base_url}
          onChange={(e) => onChange({ ...draft, base_url: e.target.value })}
        />
      </Field>
      {connector.settings_schema.map((f) => (
        <Field
          key={f.key}
          label={f.label}
          required={f.required}
          htmlFor={`setting-${f.key}`}
          aside={f.required ? undefined : "Optional"}
          hint={f.description || undefined}
        >
          <input
            id={`setting-${f.key}`}
            className="inp font-mono"
            type={f.reveal ? "text" : "password"}
            autoComplete="off"
            placeholder={placeholderOf(f, saved)}
            value={valueOf(draft, f, saved)}
            onChange={(e) => set(f.key, e.target.value)}
          />
        </Field>
      ))}
    </>
  );
}
